import { FC } from 'react';
import { Link, navigate } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';
import classNames from 'classnames';
import { DateTime } from 'luxon';
import { ButtonOutlinePrimary, ButtonUnstyled, Tooltip } from '@lambdacurry/component-library';
import { Card, CardBar, CardSection, Grid } from 'lib/components';
import { MeQuery, SubscriptionFrequency, SubscriptionStatus } from 'lib/home/generated/types/api';
import { useMyPaymentMethodsQuery, useOrdersQuery } from 'lib/home/generated/queries';
import { SUPPORT_EMAIL_ADDRESS, SUPPORT_PHONE_NUMBER } from 'lib/home/util/constants';
import { selectSubscriptionItem } from 'lib/home/util/products';
import { formatDate } from 'lib/util/date';
import {
  selectDefaultAddressFromMe,
  selectDefaultCardInfoFromPaymentMethods,
  selectFrequencyFromMe,
  selectMealPreferencesFromMe
} from '../../util/helpers';
import { SUBSCRIPTION_FREQUENCIES } from '../../util/constants';
import {
  CartPriceBreakdown,
  DefaultLayout,
  IconButtonPrimary,
  Loading,
  MealPreferenceIconLabel
} from '../../components';

const AccountPage: FC = () => {
  const { data: orders } = useOrdersQuery();
  const { data: paymentMethods } = useMyPaymentMethodsQuery();

  if (!orders || !paymentMethods) return <Loading />;

  return (
    <DefaultLayout title="Your Account" className={classNames('account-dashboard')}>
      {({ me }: { me: MeQuery['me'] }) => {
        if (me.subscription.status === SubscriptionStatus.Cancelled) {
          navigate('/');
          return null;
        }

        const { complete_order_at, medusa_cart } = orders.me.subscription.current_order;
        const nextPaymentDate = formatDate(DateTime.fromISO(complete_order_at));
        const frequency = selectFrequencyFromMe(me);
        const { diets, allergens } = selectMealPreferencesFromMe(me);
        const defaultAddress = selectDefaultAddressFromMe(me);
        const { brand, last4 } = selectDefaultCardInfoFromPaymentMethods(paymentMethods);
        const subscriptionProduct = selectSubscriptionItem(medusa_cart.items);

        return (
          <Grid className="my-4 mb-10">
            <Card className="col-span-12 grid grid-cols-10">
              <CardSection
                className="bg-account-management bg-repeat-x border-r col-span-12 md:col-span-6 flex flex-col lg:flex-row items-center justify-between pt-16 md:pt-[74px] lg:pt-20"
                variant="slim"
              >
                <StaticImage
                  className="max-w-[96px] md:max-w-[240px] md:mr-4"
                  src="../../images/placeholder-meal-plan.jpg"
                  alt="Your subscription's meal plan."
                />

                <div className="flex-1 md:ml-4 text-center lg:text-left">
                  <h2 className="h1 my-4 lg:mt-0">{subscriptionProduct?.title}</h2>

                  <ButtonOutlinePrimary
                    className="lg:mt-6 w-full md:w-auto"
                    as={buttonProps => (
                      <Link to="/account/change-plan" {...buttonProps}>
                        Change Plan
                      </Link>
                    )}
                  />
                </div>
              </CardSection>

              <div className="col-span-12 md:col-span-4 flex flex-col">
                <CardSection variant="slim">
                  <div className="flex items-center justify-between">
                    <p className="m-0">
                      <b>Diet & Options</b>
                    </p>

                    <Tooltip title="Edit Meal Preferences">
                      <IconButtonPrimary
                        as={buttonProps => <Link to="/account/preferences" {...buttonProps} />}
                        data-cy="edit-meal-preferences"
                        icon="pencil"
                      />
                    </Tooltip>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-6 mt-4 items-center">
                    {diets.map(preference => (
                      <MealPreferenceIconLabel
                        key={preference}
                        className="col-span-1 md:col-span-2 lg:col-span-1"
                        preference={preference}
                      />
                    ))}
                  </div>

                  <p className="my-0">
                    <b>Allergens</b>
                  </p>

                  <div className="grid grid-cols-2 gap-4 items-center mt-4">
                    {allergens.map(preference => (
                      <MealPreferenceIconLabel
                        className="col-span-1 md:col-span-2 lg:col-span-1"
                        preference={preference}
                      />
                    ))}
                  </div>
                </CardSection>

                <CardSection variant="slim">
                  <div className="flex items-center justify-between">
                    <p className="m-0">
                      <b>Delivery Frequency</b>
                    </p>

                    <Tooltip title="Change Subscription Frequency">
                      <IconButtonPrimary
                        as={buttonProps => <Link to="/account/change-frequency" {...buttonProps} />}
                        data-cy="change-frequency"
                        icon="pencil"
                      />
                    </Tooltip>
                  </div>

                  <p className="mb-0">
                    Every{' '}
                    {frequency === SubscriptionFrequency.OneWeek
                      ? `week`
                      : `${SUBSCRIPTION_FREQUENCIES[frequency]} weeks`}
                  </p>
                </CardSection>
              </div>
            </Card>

            <Card className="col-span-12 md:col-span-6">
              <CardBar className="flex" variant="slim">
                <h3 className="m-0">
                  {me.first_name} {me.last_name}
                </h3>

                <Tooltip title="Edit Account Info">
                  <IconButtonPrimary
                    as={buttonProps => <Link to="/account/edit" {...buttonProps} />}
                    data-cy="edit-account"
                    icon="pencil"
                  />
                </Tooltip>
              </CardBar>

              <CardSection variant="slim">
                <p>{me.phone}</p>
                <p>{me.email}</p>

                <address className="not-italic">
                  {defaultAddress.line1},<br />
                  {defaultAddress.line2 && (
                    <>
                      {defaultAddress.line2},<br />
                    </>
                  )}
                  {defaultAddress.city} {defaultAddress.state}, {defaultAddress.postal_code}
                </address>

                <p className="mb-0">••••••••••</p>

                <ButtonUnstyled
                  className="mt-auto !text-base !underline"
                  data-cy="cancel-subscription"
                  as={buttonProps => <Link {...buttonProps} to="/account/cancel" />}
                >
                  Cancel Subscription
                </ButtonUnstyled>
              </CardSection>
            </Card>

            <Card className="col-span-12 md:col-span-6">
              <CardBar variant="slim">
                <h3 className="m-0 uppercase">
                  {brand} {last4}
                </h3>

                <Tooltip title="Edit Payment Methods">
                  <IconButtonPrimary
                    as={buttonProps => <Link to="/account/payment-methods" {...buttonProps} />}
                    data-cy="edit-payment-methods"
                    icon="pencil"
                  />
                </Tooltip>
              </CardBar>

              <CardSection variant="slim">
                <p>
                  Next Payment: <b>{nextPaymentDate}</b>
                </p>

                <CartPriceBreakdown cart={medusa_cart} subscriptionFrequency={frequency} className="!mb-0" />

                <ButtonUnstyled
                  className="mt-auto !text-base !underline"
                  as={buttonProps => <Link {...buttonProps} to="/account/order-history" />}
                >
                  View Order History
                </ButtonUnstyled>
              </CardSection>
            </Card>

            <Card className="col-span-12 grid grid-cols-12 grid-rows-2 md:grid-rows-1 items-center">
              <CardSection className="col-span-12 md:col-span-8 order-2 md:order-1">
                <h3>We Also Do Corporate Catering in Los Angeles and San Francisco</h3>

                <p className="my-6">
                  To learn more, email us at <a href={`mailto:${SUPPORT_EMAIL_ADDRESS}`}>{SUPPORT_EMAIL_ADDRESS}</a> or
                  call us at <a href={`tel:${SUPPORT_PHONE_NUMBER}`}>{SUPPORT_PHONE_NUMBER}</a> and press 1.
                </p>

                <div className="flex justify-between">
                  <StaticImage
                    className="flex-1 mx-2 max-w-[80px]"
                    src="../../images/google-circle.png"
                    alt="Serving every scale of corporate clientele."
                  />

                  <StaticImage
                    className="flex-1 mx-2 max-w-[80px]"
                    src="../../images/waymo-circle.png"
                    alt="Serving every scale of corporate clientele."
                  />

                  <StaticImage
                    className="flex-1 mx-2 max-w-[80px]"
                    src="../../images/netflix-circle.png"
                    alt="Serving every scale of corporate clientele."
                  />

                  <StaticImage
                    className="flex-1 mx-2 max-w-[80px]"
                    src="../../images/abc-circle.png"
                    alt="Serving every scale of corporate clientele."
                  />

                  <StaticImage
                    className="flex-1 mx-2 max-w-[80px]"
                    src="../../images/nba-circle.png"
                    alt="Serving every scale of corporate clientele."
                  />
                </div>
              </CardSection>

              <div className="bg-corporate-catering bg-cover bg-center col-span-12 md:col-span-4 h-full order-1 md:order-2" />
            </Card>
          </Grid>
        );
      }}
    </DefaultLayout>
  );
};

export default AccountPage;
